import { Head, Link, router, usePage } from "@inertiajs/react"
import {
  ArrowRight,
  BarChart3,
  Calculator,
  ChevronDown,
  CreditCard,
  Landmark,
  Target,
  TrendingUp,
  Wallet,
} from "lucide-react"

import AppLogoIcon from "@/components/app-logo-icon"
import { DemoAccessButton } from "@/components/demo-access-button"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Progress } from "@/components/ui/progress"
import { UserMenuContent } from "@/components/user-menu-content"
import { useInitials } from "@/hooks/use-initials"
import {
  dashboardPath,
  expensesPath,
  fiCalculatorPath,
  goalsPath,
  newExpensePath,
  portfoliosPath,
  sessionPath,
  settingsProfilePath,
  signInPath,
  signUpPath,
} from "@/routes"
import type { SharedData } from "@/types"

const featureCards = [
  {
    title: "Track expenses & income",
    description:
      "Log spending and paychecks by category, then watch your savings rate move in real time.",
    icon: CreditCard,
  },
  {
    title: "Manage portfolios",
    description:
      "Positions, dividends, cost basis, and returns tracked across every account you hold.",
    icon: BarChart3,
  },
  {
    title: "Track liabilities",
    description:
      "Debt balances, rates, and payoff timelines factored into your true net worth.",
    icon: Landmark,
  },
  {
    title: "Set FI goals",
    description:
      "Targets like an emergency fund, a house down payment, or full financial independence.",
    icon: Target,
  },
  {
    title: "Run FIRE scenarios",
    description:
      "Compare Lean, Fat, Coast, and Barista FIRE paths, plus Monte Carlo success odds.",
    icon: Calculator,
  },
  {
    title: "One dashboard",
    description:
      "Net worth, FI progress, and every account, in a single view built for daily use.",
    icon: Wallet,
  },
]

const howItWorks = [
  {
    step: "01",
    title: "Log your numbers",
    description:
      "Add expenses, income, portfolios, and debts as you go, or import a CSV to backfill your history in minutes.",
  },
  {
    step: "02",
    title: "Set your targets",
    description:
      "Define your FIRE number, safe withdrawal rate, and goals, an emergency fund, a house, or full independence.",
  },
  {
    step: "03",
    title: "Track your progress",
    description:
      "Net worth, savings rate, and FI progress update automatically. Run Coast, Barista, and Monte Carlo scenarios anytime.",
  },
]

const fireFacts = [
  { label: "FIRE Number", value: "25× annual expenses" },
  { label: "Safe Withdrawal Rate", value: "4% per year" },
  { label: "In one place", value: "Expenses, goals & portfolios" },
]

interface QuickStats {
  fi_number: number
  net_worth: number
  fi_progress_percentage: number
  active_goals_count: number
  has_data: boolean
}

interface HomeProps {
  quick_stats: QuickStats | null
}

export default function Welcome({ quick_stats }: HomeProps) {
  const page = usePage<SharedData>()
  const { auth } = page.props
  const getInitials = useInitials()

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(amount || 0)

  const handleLogout = () => router.flushAll()

  return (
    <>
      <Head title="FIREFlow" />

      <div className="relative min-h-screen">
        <div className="app-grid-bg pointer-events-none fixed inset-0 -z-10" />
        <div className="hero-orb pointer-events-none fixed top-8 left-[-6rem] -z-10 h-72 w-72 rounded-full bg-[radial-gradient(circle,_rgba(82,116,255,0.18)_0%,_transparent_72%)]" />
        <div className="hero-orb pointer-events-none fixed top-0 right-[-8rem] -z-10 h-[28rem] w-[28rem] rounded-full bg-[radial-gradient(circle,_rgba(57,104,214,0.2)_0%,_transparent_68%)]" />

        {/* Header */}
        <header className="border-border/60 bg-background/75 sticky top-0 z-30 border-b backdrop-blur-xl">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8">
            <Link href="/" className="flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-xl bg-[linear-gradient(135deg,rgba(91,127,255,0.95),rgba(42,83,186,0.92))] text-white shadow-lg shadow-slate-900/20">
                <AppLogoIcon className="size-7 text-white" />
              </div>
              <span className="text-lg font-semibold tracking-tight">
                FIREFlow
              </span>
            </Link>

            <nav className="text-muted-foreground hidden items-center gap-8 text-sm font-medium md:flex">
              <a href="#features" className="hover:text-foreground transition">
                Features
              </a>
              <a
                href="#how-it-works"
                className="hover:text-foreground transition"
              >
                How it works
              </a>
              <a href="#method" className="hover:text-foreground transition">
                FIRE method
              </a>
            </nav>

            <div className="flex items-center gap-3">
              {auth.user ? (
                <>
                  <Link
                    href={dashboardPath()}
                    className="hidden items-center gap-2 rounded-full bg-slate-950 px-4 py-2 text-sm font-medium text-white shadow-lg shadow-slate-900/15 transition hover:bg-slate-800 sm:inline-flex dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
                  >
                    Dashboard
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="border-border/70 flex items-center gap-2 rounded-full border bg-white/70 py-1.5 pr-3 pl-1.5 text-sm font-medium transition hover:bg-white dark:bg-white/5 dark:hover:bg-white/10"
                      >
                        <Avatar className="h-7 w-7">
                          <AvatarImage
                            src={auth.user.avatar}
                            alt={auth.user.name}
                          />
                          <AvatarFallback className="bg-neutral-200 text-xs text-black dark:bg-neutral-700 dark:text-white">
                            {getInitials(auth.user.name)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="hidden sm:inline">
                          {auth.user.name.split(" ")[0]}
                        </span>
                        <ChevronDown className="text-muted-foreground h-3.5 w-3.5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <UserMenuContent
                        auth={{ session: auth.session, user: auth.user }}
                      />
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <>
                  <Link
                    href={signInPath()}
                    className="border-border/80 text-foreground inline-flex items-center rounded-full border bg-white/70 px-4 py-2 text-sm font-medium transition hover:bg-white dark:bg-white/5 dark:hover:bg-white/10"
                  >
                    Log in
                  </Link>
                  <Link
                    href={signUpPath()}
                    className="inline-flex items-center gap-2 rounded-full bg-[linear-gradient(135deg,rgba(91,127,255,0.95),rgba(42,83,186,0.92))] px-4 py-2 text-sm font-medium text-white shadow-lg shadow-blue-950/20 transition hover:scale-[1.01]"
                  >
                    Sign up
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Hero */}
        <section className="mx-auto max-w-7xl px-6 pt-16 pb-20 lg:px-8 lg:pt-24">
          <div className="grid gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            <div className="max-w-2xl">
              <div className="section-kicker">
                {auth.user ? (
                  <>
                    Signed in as <strong>{auth.user.email}</strong>
                  </>
                ) : (
                  <>
                    FIRE stands for{" "}
                    <strong>Financial Independence, Retire Early</strong>
                  </>
                )}
              </div>

              <h1 className="mt-5 font-serif text-5xl leading-[0.98] tracking-tight text-balance sm:text-6xl lg:text-7xl">
                {auth.user ? (
                  <>Welcome back, {auth.user.name.split(" ")[0]}.</>
                ) : (
                  <>
                    Wealth clarity,
                    <br />
                    in one dashboard.
                  </>
                )}
              </h1>

              <p className="text-muted-foreground mt-5 max-w-xl text-lg leading-8">
                {auth.user && quick_stats
                  ? quick_stats.has_data
                    ? `You're ${quick_stats.fi_progress_percentage.toFixed(1)}% of the way to your FIRE number of ${formatCurrency(quick_stats.fi_number)}.`
                    : "Add an expense or a portfolio to start tracking your progress toward financial independence."
                  : "Expenses, goals, portfolio tracking, and FIRE progress without the clutter."}
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  href={auth.user ? dashboardPath() : signUpPath()}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-950 px-6 py-3 text-sm font-semibold text-white shadow-xl shadow-slate-900/15 transition hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
                >
                  {auth.user ? "Go to dashboard" : "Create your workspace"}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                {auth.user && quick_stats && !quick_stats.has_data ? (
                  <Link
                    href={newExpensePath()}
                    className="border-border/80 text-foreground inline-flex items-center justify-center gap-2 rounded-full border bg-white/75 px-6 py-3 text-sm font-semibold transition hover:bg-white dark:bg-white/5 dark:hover:bg-white/10"
                  >
                    Add your first expense
                  </Link>
                ) : null}
                {!auth.user ? (
                  <DemoAccessButton
                    className="sm:w-auto"
                    label="Try demo data"
                  />
                ) : null}
              </div>

              <div className="mt-10 grid gap-4 sm:grid-cols-3">
                {auth.user && quick_stats?.has_data ? (
                  <>
                    <div className="metric-card py-6">
                      <div className="text-muted-foreground text-xs font-semibold tracking-[0.22em] uppercase">
                        Net Worth
                      </div>
                      <div className="mt-3 text-2xl font-semibold tracking-tight lg:text-3xl">
                        {formatCurrency(quick_stats.net_worth)}
                      </div>
                    </div>
                    <div className="metric-card py-6">
                      <div className="text-muted-foreground text-xs font-semibold tracking-[0.22em] uppercase">
                        FI Progress
                      </div>
                      <div className="mt-3 text-2xl font-semibold tracking-tight lg:text-3xl">
                        {quick_stats.fi_progress_percentage.toFixed(1)}%
                      </div>
                    </div>
                    <div className="metric-card py-6">
                      <div className="text-muted-foreground text-xs font-semibold tracking-[0.22em] uppercase">
                        Active Goals
                      </div>
                      <div className="mt-3 text-2xl font-semibold tracking-tight lg:text-3xl">
                        {quick_stats.active_goals_count}
                      </div>
                    </div>
                  </>
                ) : (
                  fireFacts.map((item) => (
                    <div key={item.label} className="metric-card py-6">
                      <div className="text-muted-foreground text-xs font-semibold tracking-[0.22em] uppercase">
                        {item.label}
                      </div>
                      <div className="mt-3 text-2xl font-semibold tracking-tight lg:text-3xl">
                        {item.value}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="app-panel relative overflow-hidden p-6 lg:p-8">
              <div className="absolute inset-x-0 top-0 h-28 bg-[linear-gradient(180deg,rgba(91,127,255,0.14),transparent)]" />
              <div className="relative">
                {auth.user && quick_stats?.has_data ? (
                  <>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-muted-foreground text-sm">
                          Your FI snapshot
                        </p>
                        <h2 className="mt-1 text-2xl font-semibold tracking-tight">
                          {quick_stats.fi_progress_percentage.toFixed(1)}% to
                          FIRE
                        </h2>
                      </div>
                      <div className="rounded-2xl bg-[linear-gradient(135deg,rgba(91,127,255,0.95),rgba(42,83,186,0.92))] p-3 text-white shadow-lg">
                        <TrendingUp className="h-6 w-6" />
                      </div>
                    </div>

                    <Progress
                      value={quick_stats.fi_progress_percentage}
                      className="mt-6"
                    />
                    <p className="text-muted-foreground mt-2 text-xs">
                      FIRE number: {formatCurrency(quick_stats.fi_number)} (25×
                      annual expenses)
                    </p>

                    <div className="mt-8 grid gap-3 sm:grid-cols-2">
                      <Link
                        href={expensesPath()}
                        className="border-border/70 hover:border-border flex items-center gap-3 rounded-[24px] border bg-white/78 p-4 transition dark:bg-white/5"
                      >
                        <div className="text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,rgba(91,127,255,0.14),rgba(42,83,186,0.12))]">
                          <CreditCard className="h-4 w-4" />
                        </div>
                        <span className="text-sm font-semibold tracking-tight">
                          Expenses
                        </span>
                      </Link>
                      <Link
                        href={goalsPath()}
                        className="border-border/70 hover:border-border flex items-center gap-3 rounded-[24px] border bg-white/78 p-4 transition dark:bg-white/5"
                      >
                        <div className="text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,rgba(91,127,255,0.14),rgba(42,83,186,0.12))]">
                          <Target className="h-4 w-4" />
                        </div>
                        <span className="text-sm font-semibold tracking-tight">
                          Goals
                        </span>
                      </Link>
                      <Link
                        href={dashboardPath()}
                        className="border-border/70 hover:border-border flex items-center gap-3 rounded-[24px] border bg-white/78 p-4 transition dark:bg-white/5"
                      >
                        <div className="text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,rgba(91,127,255,0.14),rgba(42,83,186,0.12))]">
                          <BarChart3 className="h-4 w-4" />
                        </div>
                        <span className="text-sm font-semibold tracking-tight">
                          Portfolios
                        </span>
                      </Link>
                      <Link
                        href={fiCalculatorPath()}
                        className="border-border/70 hover:border-border flex items-center gap-3 rounded-[24px] border bg-white/78 p-4 transition dark:bg-white/5"
                      >
                        <div className="text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,rgba(91,127,255,0.14),rgba(42,83,186,0.12))]">
                          <Calculator className="h-4 w-4" />
                        </div>
                        <span className="text-sm font-semibold tracking-tight">
                          FIRE Calculator
                        </span>
                      </Link>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-muted-foreground text-sm">
                          {auth.user ? "Get started" : "Live progress"}
                        </p>
                        <h2 className="mt-1 text-2xl font-semibold tracking-tight">
                          {auth.user
                            ? "What you can track"
                            : "Designed to move fast"}
                        </h2>
                      </div>
                      <div className="rounded-2xl bg-[linear-gradient(135deg,rgba(91,127,255,0.95),rgba(42,83,186,0.92))] p-3 text-white shadow-lg">
                        <TrendingUp className="h-6 w-6" />
                      </div>
                    </div>

                    <div className="mt-8 grid gap-3 sm:grid-cols-2">
                      {featureCards.slice(0, 4).map((feature) => (
                        <div
                          key={feature.title}
                          className="border-border/70 rounded-[24px] border bg-white/78 p-5 dark:bg-white/5"
                        >
                          <div className="text-primary flex h-11 w-11 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,rgba(91,127,255,0.14),rgba(42,83,186,0.12))]">
                            <feature.icon className="h-5 w-5" />
                          </div>
                          <p className="mt-4 text-sm font-semibold tracking-tight">
                            {feature.title}
                          </p>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section
          id="how-it-works"
          className="border-border/60 border-t bg-white/40 py-20 dark:bg-white/[0.02]"
        >
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="max-w-2xl">
              <div className="section-kicker">How it works</div>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">
                From first expense to full FIRE.
              </h2>
              <p className="text-muted-foreground mt-3 text-lg">
                Three steps, one dashboard.
              </p>
            </div>

            <div className="mt-12 grid gap-10 md:grid-cols-3">
              {howItWorks.map((item) => (
                <div key={item.step}>
                  <div className="text-primary text-sm font-semibold tracking-wide">
                    {item.step}
                  </div>
                  <h3 className="mt-3 text-lg font-semibold tracking-tight">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground mt-2 text-sm leading-6">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section id="features" className="py-20">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="max-w-2xl">
              <div className="section-kicker">Features</div>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">
                Everything your FIRE plan needs.
              </h2>
              <p className="text-muted-foreground mt-3 text-lg">
                No spreadsheets, no separate apps for expenses, goals, and
                investments.
              </p>
            </div>

            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {featureCards.map((feature) => (
                <div
                  key={feature.title}
                  className="border-border/70 rounded-[24px] border bg-white/70 p-6 dark:bg-white/[0.03]"
                >
                  <div className="text-primary flex h-11 w-11 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,rgba(91,127,255,0.14),rgba(42,83,186,0.12))]">
                    <feature.icon className="h-5 w-5" />
                  </div>
                  <p className="mt-4 text-sm font-semibold tracking-tight">
                    {feature.title}
                  </p>
                  <p className="text-muted-foreground mt-1.5 text-sm leading-6">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Method */}
        <section
          id="method"
          className="border-border/60 border-t bg-white/40 py-20 dark:bg-white/[0.02]"
        >
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="max-w-2xl">
              <div className="section-kicker">The FIRE method</div>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">
                Built on the same math you&apos;d do by hand.
              </h2>
              <p className="text-muted-foreground mt-3 text-lg">
                No black boxes, just the standard FIRE formulas, automated.
              </p>
            </div>

            <div className="mt-12 grid gap-4 sm:grid-cols-3">
              {fireFacts.map((item) => (
                <div key={item.label} className="metric-card py-6">
                  <div className="text-muted-foreground text-xs font-semibold tracking-[0.22em] uppercase">
                    {item.label}
                  </div>
                  <div className="mt-3 text-2xl font-semibold tracking-tight lg:text-3xl">
                    {item.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA banner */}
        <section className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <div className="relative overflow-hidden rounded-[32px] bg-[linear-gradient(135deg,rgba(15,23,42,0.97),rgba(30,41,59,0.95))] px-8 py-16 text-center shadow-2xl sm:px-16">
            <div className="hero-orb pointer-events-none absolute top-0 right-[-4rem] h-72 w-72 rounded-full bg-[radial-gradient(circle,_rgba(91,127,255,0.35)_0%,_transparent_70%)]" />
            <div className="relative">
              <h2 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
                {auth.user
                  ? "Pick up where you left off."
                  : "Start your FIRE plan today."}
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-white/70">
                {auth.user
                  ? "Your dashboard, expenses, and portfolios are one click away."
                  : "Free to start. Try it with demo data before you add your own numbers."}
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Link
                  href={auth.user ? dashboardPath() : signUpPath()}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-slate-950 shadow-xl transition hover:bg-slate-100"
                >
                  {auth.user ? "Go to dashboard" : "Create your workspace"}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                {!auth.user ? (
                  <DemoAccessButton
                    className="border-white/20 bg-white/10 text-white hover:bg-white/15 sm:w-auto"
                    label="Try demo data"
                  />
                ) : null}
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-border/60 border-t">
          <div className="mx-auto max-w-7xl px-6 py-12 lg:px-8">
            <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
              <div className="col-span-2 lg:col-span-1">
                <Link href="/" className="flex items-center gap-3">
                  <div className="flex size-9 items-center justify-center rounded-xl bg-[linear-gradient(135deg,rgba(91,127,255,0.95),rgba(42,83,186,0.92))] text-white shadow-lg shadow-slate-900/20">
                    <AppLogoIcon className="size-6 text-white" />
                  </div>
                  <span className="text-base font-semibold tracking-tight">
                    FIREFlow
                  </span>
                </Link>
                <p className="text-muted-foreground mt-4 max-w-xs text-sm leading-6">
                  Expenses, goals, portfolio tracking, and FIRE progress in one
                  dashboard.
                </p>
              </div>

              <div>
                <div className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                  Product
                </div>
                <ul className="mt-4 space-y-3 text-sm">
                  <li>
                    <Link
                      href={dashboardPath()}
                      className="text-muted-foreground hover:text-foreground transition"
                    >
                      Dashboard
                    </Link>
                  </li>
                  <li>
                    <Link
                      href={fiCalculatorPath()}
                      className="text-muted-foreground hover:text-foreground transition"
                    >
                      FIRE Calculator
                    </Link>
                  </li>
                  <li>
                    <Link
                      href={portfoliosPath()}
                      className="text-muted-foreground hover:text-foreground transition"
                    >
                      Portfolios
                    </Link>
                  </li>
                  <li>
                    <Link
                      href={goalsPath()}
                      className="text-muted-foreground hover:text-foreground transition"
                    >
                      Goals
                    </Link>
                  </li>
                </ul>
              </div>

              <div>
                <div className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                  Account
                </div>
                <ul className="mt-4 space-y-3 text-sm">
                  {auth.user ? (
                    <>
                      <li>
                        <Link
                          href={settingsProfilePath()}
                          className="text-muted-foreground hover:text-foreground transition"
                        >
                          Settings
                        </Link>
                      </li>
                      <li>
                        <Link
                          method="delete"
                          href={sessionPath({ id: auth.session.id })}
                          as="button"
                          onClick={handleLogout}
                          className="text-muted-foreground hover:text-foreground cursor-pointer transition"
                        >
                          Log out
                        </Link>
                      </li>
                    </>
                  ) : (
                    <>
                      <li>
                        <Link
                          href={signInPath()}
                          className="text-muted-foreground hover:text-foreground transition"
                        >
                          Log in
                        </Link>
                      </li>
                      <li>
                        <Link
                          href={signUpPath()}
                          className="text-muted-foreground hover:text-foreground transition"
                        >
                          Sign up
                        </Link>
                      </li>
                    </>
                  )}
                </ul>
              </div>

              <div>
                <div className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                  FIRE method
                </div>
                <ul className="text-muted-foreground mt-4 space-y-3 text-sm">
                  <li>25× annual expenses = FIRE number</li>
                  <li>4% safe withdrawal rate</li>
                </ul>
              </div>
            </div>

            <div className="border-border/60 text-muted-foreground mt-12 flex flex-col items-center justify-between gap-4 border-t pt-8 text-xs sm:flex-row">
              <span>
                &copy; {new Date().getFullYear()} FIREFlow. Built for the FIRE
                community.
              </span>
            </div>
          </div>
        </footer>
      </div>
    </>
  )
}
